import { createApiClient } from '@chikara/utils';
import { API_BASE_URL, APP_CONFIG } from '@/config';
import { ApiError, BackendErrorPayload } from '@chikara/types';
import { mapApiError } from '@/features/auth/utils/mapApiError';
import { tokenStorage } from '@/features/auth/utils/tokenStorage';
import { companyStorage } from '@/features/auth/utils/companyStorage';
import { ensureAccessTokenFresh } from '@/features/auth/utils/ensureAccessTokenFresh';
import { API_ENDPOINTS } from '@/config';
import { sanitizeRequestUrl, trackError } from '@/shared/analytics/errors';
import type { ApiNetworkErrorContext, ApiRequestErrorContext } from '@chikara/utils';

const TOKEN_KEY = APP_CONFIG.session.tokenKey;
const REFRESH_TOKEN_KEY = APP_CONFIG.session.refreshTokenKey;

function getRealAccessToken(): string | null {
  const token = tokenStorage.getAccessTokenOptional();
  if (!token) return null;
  // Never treat mock tokens as "real" for Authorization
  if (token.endsWith('.mock_signature')) return null;
  return token;
}

const PUBLIC_ENDPOINTS = new Set<string>([
  API_ENDPOINTS.auth.login,
  API_ENDPOINTS.auth.preLogin,
  API_ENDPOINTS.auth.refreshToken,
  API_ENDPOINTS.auth.register,
  API_ENDPOINTS.auth.forgotPassword,
  API_ENDPOINTS.auth.resetPassword,
  API_ENDPOINTS.onboarding.validateInvite,
  API_ENDPOINTS.onboarding.acceptInvite,
  // Must be called WITHOUT an Authorization header: the single-use token in the
  // request body is the only credential. If the browser already holds a session
  // for a different user (e.g. the admin/QA who sent the invite is logged in),
  // attaching it makes the backend reject the request with 007.004.401
  // "El token fue modificado o no coincide". That 401 also clears the session
  // (onUnauthorized), which is why a refresh/retry — now session-less — works.
  API_ENDPOINTS.onboarding.setupPassword,
  API_ENDPOINTS.onboarding.customerIdValidate,
  API_ENDPOINTS.onboarding.userSelfOnboard,
]);

/**
 * Endpoints whose failures must NOT trigger the global error toast.
 * The caller (hook) is responsible for showing a user-friendly toast/inline
 * message instead of the raw backend message.
 */
const SILENT_ERROR_ENDPOINTS = [
  API_ENDPOINTS.onboarding.customerIdValidate,
  API_ENDPOINTS.onboarding.userSelfOnboard,
  // La validación en la carga del onboarding pinta su propia pantalla de error
  // (OnboardingErrorScreen); el toast global encima sería redundante.
  API_ENDPOINTS.onboarding.validateInvite,
  API_ENDPOINTS.auth.logout,
];

function isSilentErrorEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  return SILENT_ERROR_ENDPOINTS.some((endpoint) => url.includes(endpoint));
}

/**
 * Configured API client for the platform app.
 * All services should use this instance for consistent configuration.
 */
export const apiClient = createApiClient({
  baseUrl: API_BASE_URL,
  getToken: () => getRealAccessToken(),
  shouldAttachAuth: (endpoint) => !PUBLIC_ENDPOINTS.has(endpoint),
  beforeAuthenticatedRequest: ensureAccessTokenFresh,
  onUnauthorized: () => {
    const hadSession = !!tokenStorage.getAccessTokenOptional();
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    if (hadSession) {
      companyStorage.clearCompanies();
    }
    window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { hadSession } }));
  },
  onError: async ({ response, endpoint, method, requestUrl }: ApiRequestErrorContext) => {
    let backendPayload: BackendErrorPayload | undefined;

    try {
      backendPayload = await response.json();
    } catch {
      // response without body
    }

    const apiError = mapApiError(backendPayload);
    const { url: sanitizedUrl, path: requestPath } = sanitizeRequestUrl(requestUrl);

    if (!isSilentErrorEndpoint(endpoint)) {
      window.dispatchEvent(
        new CustomEvent<ApiError>('api:error', { detail: apiError })
      );
    }

    if (response.status === 401) {
      window.dispatchEvent(new Event('auth:unauthorized'));
    }

    trackError({
      source: 'apiClient',
      category: 'api',
      error: apiError,
      context: {
        httpStatus: response.status,
        responseStatusText: response.statusText,
        request: {
          method,
          endpoint,
          url: sanitizedUrl,
          path: requestPath,
        },
      },
    });

    throw apiError;
  },
  onNetworkError: ({ error, endpoint, method, requestUrl }: ApiNetworkErrorContext) => {
    const { url: sanitizedUrl, path: requestPath } = sanitizeRequestUrl(requestUrl);
    const apiError: ApiError = {
      type: 'network',
      status: 400,
      message:
        error instanceof Error
          ? error.message
          : 'Network error',
    };

    window.dispatchEvent(
      new CustomEvent<ApiError>('api:error', { detail: apiError })
    );

    trackError({
      source: 'apiClient',
      category: 'network',
      error: apiError,
      context: {
        request: {
          method,
          endpoint,
          url: sanitizedUrl,
          path: requestPath,
        },
        networkCause:
          error instanceof Error ? error.name : typeof error,
      },
    });

    throw apiError;
  },
});
